// Stop-frame tutorial overlay (design §2.3 教学约束 v0.5.2).
//
// The overlay is the *only* thing listening while a tutorial stop is held: the
// session disables `InputController` before showing it, so nothing here can be
// double-recorded. The gesture that dismisses is the gesture that gets injected
// into the frozen frame, which is why the dismissal rules have to mirror
// `GestureRecognizer` exactly rather than approximate it:
//
//   jump  — any pointerdown, or Space. A tap is a jump; there is no threshold.
//   slide — pointerdown then ≥ SLIDE_THRESHOLD_PX of downward travel, or
//           ArrowDown. A plain tap must NOT pass: dismissing the bar step with a
//           tap would inject a jump straight into the bar, i.e. teach the exact
//           death the step exists to prevent. A tap shakes the card instead.

import { t } from '../i18n';
import { SLIDE_THRESHOLD_PX } from '../game/input';
import type { TutorialStep } from '../game/tutorial';
import { el, enter } from './dom';

export interface TutorialOverlayOptions {
  step: TutorialStep;
  /** The required gesture happened — resume and inject. */
  onAct: () => void;
}

export interface TutorialOverlay {
  remove(): void;
}

const COPY: Record<TutorialStep, 'tutorial.jump' | 'tutorial.slide'> = {
  jump: 'tutorial.jump',
  slide: 'tutorial.slide',
};

const isSpace = (e: KeyboardEvent): boolean => e.key === ' ' || e.code === 'Space';
const isArrowDown = (e: KeyboardEvent): boolean =>
  e.key === 'ArrowDown' || e.code === 'ArrowDown';

export function showTutorialStep(
  root: HTMLElement,
  opts: TutorialOverlayOptions,
): TutorialOverlay {
  const wantsSlide = opts.step === 'slide';

  const card = el('div', { class: 'tut-card' }, [
    el('div', { class: 'tut-line', text: t(COPY[opts.step]) }),
    wantsSlide
      ? el('div', { class: 'tut-ind tut-ind--slide' }, [
          el('span', { class: 'tut-arrow' }),
          el('span', { class: 'tut-arrow' }),
        ])
      : el('div', { class: 'tut-ind tut-ind--tap' }, [el('span', { class: 'tut-dot' })]),
  ]);

  const screen = el(
    'div',
    {
      class: `screen screen--tutorial tutorial--${opts.step}`,
      attrs: { role: 'button', 'aria-label': t(COPY[opts.step]) },
    },
    [card],
  );

  let done = false;
  let pointerId: number | null = null;
  let startY = 0;
  let maxY = 0;
  let shakeTimer: ReturnType<typeof setTimeout> | null = null;

  const shake = (): void => {
    card.classList.remove('is-shake');
    // Force a reflow so re-adding the class restarts the animation.
    void card.offsetWidth;
    card.classList.add('is-shake');
    if (shakeTimer !== null) clearTimeout(shakeTimer);
    shakeTimer = setTimeout(() => card.classList.remove('is-shake'), 460);
  };

  const act = (): void => {
    if (done) return;
    done = true;
    opts.onAct();
  };

  const onPointerDown = (e: Event): void => {
    const ev = e as PointerEvent;
    // The session re-enables input synchronously inside `onAct`; without this the
    // very same pointerdown bubbles to the stage listener and records a second,
    // duplicate action (the bug showIntro already had to fix).
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    if (!wantsSlide) {
      act();
      return;
    }
    if (pointerId !== null) return; // one finger at a time, like the recognizer
    pointerId = ev.pointerId;
    startY = ev.clientY;
    maxY = ev.clientY;
    try {
      screen.setPointerCapture(ev.pointerId);
    } catch {
      /* capture is an optimisation; the overlay covers the stage anyway */
    }
  };

  const onPointerMove = (e: Event): void => {
    if (!wantsSlide || done) return;
    const ev = e as PointerEvent;
    if (pointerId !== ev.pointerId) return;
    e.stopPropagation();
    if (ev.clientY > maxY) maxY = ev.clientY;
    if (maxY - startY >= SLIDE_THRESHOLD_PX) act();
  };

  const onPointerUp = (e: Event): void => {
    if (!wantsSlide || done) return;
    const ev = e as PointerEvent;
    if (pointerId !== ev.pointerId) return;
    e.stopPropagation();
    pointerId = null;
    // Released without ever travelling far enough: that was a tap, and a tap is
    // a jump. Say no, visibly, instead of teaching the wrong verb.
    shake();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (done) return;
    if (wantsSlide) {
      if (isArrowDown(e)) {
        e.preventDefault();
        act();
      } else if (isSpace(e)) {
        e.preventDefault();
        shake();
      }
      return;
    }
    if (isSpace(e)) {
      e.preventDefault();
      act();
    }
  };

  screen.addEventListener('pointerdown', onPointerDown);
  screen.addEventListener('pointermove', onPointerMove);
  screen.addEventListener('pointerup', onPointerUp);
  screen.addEventListener('pointercancel', onPointerUp);
  window.addEventListener('keydown', onKeyDown);

  root.append(screen);
  enter(screen);

  return {
    remove(): void {
      done = true;
      if (shakeTimer !== null) clearTimeout(shakeTimer);
      window.removeEventListener('keydown', onKeyDown);
      screen.remove();
    },
  };
}
